// The session a V2 onboarding wizard (athlete or coach) runs in.
// localStorage is shared by every tab of the site, so a log in or log out in
// another tab rewrites the token this tab sends. (An admin "open as user"
// tab reads its own per-tab copy through the impersonation overlay, and
// these reads go through it too.) A wizard binds to the account it opened
// with; once the stored session belongs to someone else, or is gone, its
// services send nothing more and the wizard is told to stop. Callers that
// never bind (the edit hub, the verification screen) are left as they were.
// A wizard binds through ./useWizardSession, which also stops it when another
// tab changes the session.

export const SESSION_CHANGED = "session_changed";
const SESSION_MESSAGE = "You signed out or signed in to a different account in another tab. Reload this page to continue.";

let bound = null;

const readSession = () => ({
  token: localStorage.getItem("token") || "",
  email: (localStorage.getItem("myEmail") || "").trim().toLowerCase(),
});

// Binds the wizard to the stored session. `onChange` runs when a service
// refuses to send for another account. Returns the release function; call
// it when the wizard unmounts.
export const bindSession = (onChange) => {
  const binding = { ...readSession(), serverEmail: "", onChange };
  bound = binding;
  return () => {
    if (bound === binding) bound = null;
  };
};

// True once the stored session no longer belongs to the bound account. A new
// token for the same email is the same account signing in again.
export const sessionChanged = () => {
  if (!bound) return false;
  const now = readSession();
  if (!now.token) return true;
  if (now.token === bound.token) return false;
  return !(bound.email && now.email === bound.email);
};

// The error a service throws instead of sending; it tells the bound wizard.
export const sessionError = () => {
  const err = new Error(SESSION_MESSAGE);
  err.code = SESSION_CHANGED;
  if (bound?.onChange) bound.onChange();
  return err;
};

// The token to send, after checking it still belongs to the bound account.
// Throws sessionError() once it does not.
export const guardSession = () => {
  if (!bound) return localStorage.getItem("token");
  if (sessionChanged()) throw sessionError();
  bound.token = readSession().token;
  return bound.token;
};

// An onboarding state names the account the server answered for (`email`).
// The first answer pins it; a later answer for anyone else throws.
export const checkAnsweredFor = (data) => {
  if (!bound || !data || typeof data.email !== "string" || !data.email) return;
  const email = data.email.trim().toLowerCase();
  if (!bound.serverEmail) bound.serverEmail = email;
  else if (bound.serverEmail !== email) throw sessionError();
};
